import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
// @mui
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Stack,
  LinearProgress,
  Chip,
  alpha,
  useTheme,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import LockResetRoundedIcon from "@mui/icons-material/LockResetRounded";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";

import authService from "../services/auth.service";
import { LOGIN_SUCCESS } from "../actions/types";

// Strong password generator helper
const generateStrongPassword = (length = 14) => {
  const uppers = "ABCDEFGHJKLMNPQRSTUVWXYZ";
  const lowers = "abcdefghijkmnopqrstuvwxyz";
  const numbers = "23456789";
  const symbols = "!@#$%^&*";
  const all = uppers + lowers + numbers + symbols;

  const getRandom = (charset) => {
    if (typeof window !== "undefined" && window.crypto && window.crypto.getRandomValues) {
      const arr = new Uint32Array(1);
      window.crypto.getRandomValues(arr);
      return charset[arr[0] % charset.length];
    }
    return charset[Math.floor(Math.random() * charset.length)];
  };

  const chars = [
    getRandom(uppers),
    getRandom(lowers),
    getRandom(numbers),
    getRandom(symbols),
  ];
  while (chars.length < length) {
    chars.push(getRandom(all));
  }
  return chars.sort(() => Math.random() - 0.5).join("");
};

export default function FirstTimePasswordModal() {
  const theme = useTheme();
  const dispatch = useDispatch();

  const { user: reduxUser } = useSelector((state) => state.auth || {});

  const getStoredUser = () => {
    try {
      const s = localStorage.getItem("user");
      return s ? JSON.parse(s) : null;
    } catch {
      return null;
    }
  };

  const currentUser = reduxUser || getStoredUser();
  const mustChange = Boolean(currentUser?.mustChangePassword);

  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState({ type: "", text: "" });
  const [copied, setCopied] = useState(false);
  const [dismissedBanner, setDismissedBanner] = useState(false);

  // Auto-open modal on first login if mustChangePassword is true
  useEffect(() => {
    if (mustChange) {
      setOpen(true);
    }
  }, [mustChange]);

  if (!mustChange) {
    return null;
  }

  // Calculate password strength
  const getPasswordStrength = (pass) => {
    if (!pass) return { score: 0, label: "Empty", color: "error" };
    let score = 0;
    if (pass.length >= 6) score += 25;
    if (pass.length >= 8) score += 25;
    if (/[A-Z]/.test(pass) && /[a-z]/.test(pass)) score += 25;
    if (/\d/.test(pass) && /[^A-Za-z0-9]/.test(pass)) score += 25;

    if (score <= 25) return { score, label: "Weak", color: "error" };
    if (score <= 50) return { score, label: "Fair", color: "warning" };
    if (score <= 75) return { score, label: "Good", color: "info" };
    return { score: 100, label: "Strong", color: "success" };
  };

  const strength = getPasswordStrength(formData.newPassword);

  const handleSuggest = () => {
    const strong = generateStrongPassword(14);
    setFormData((prev) => ({
      ...prev,
      newPassword: strong,
      confirmPassword: strong,
    }));
    setShowNew(true);
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(strong).catch(() => {});
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    }
    setMsg({
      type: "info",
      text: "Suggested strong password generated and copied to clipboard!",
    });
  };

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setMsg({ type: "", text: "" });

    if (!formData.currentPassword) {
      return setMsg({ type: "error", text: "Please enter your current/temporary password." });
    }
    if (!formData.newPassword) {
      return setMsg({ type: "error", text: "Please enter a new password." });
    }
    if (formData.newPassword.length < 6) {
      return setMsg({ type: "error", text: "New password must be at least 6 characters long." });
    }
    if (formData.newPassword !== formData.confirmPassword) {
      return setMsg({ type: "error", text: "New password and confirmation do not match." });
    }
    if (formData.newPassword === formData.currentPassword) {
      return setMsg({ type: "error", text: "New password must be different from your temporary password." });
    }

    setLoading(true);
    try {
      const res = await authService.changePassword(formData.currentPassword, formData.newPassword);

      // Successfully updated: clear mustChangePassword in stored user and Redux
      const stored = getStoredUser() || {};
      const updatedUser = {
        ...stored,
        mustChangePassword: false,
      };
      localStorage.setItem("user", JSON.stringify(updatedUser));
      dispatch({
        type: LOGIN_SUCCESS,
        payload: { user: updatedUser },
      });

      setMsg({
        type: "success",
        text: res?.data?.message || "Password changed successfully! Your account is now secure.",
      });

      setTimeout(() => {
        setOpen(false);
      }, 1500);
    } catch (err) {
      const errMsg = err?.response?.data?.message || err.message || "Failed to change password. Please check your current password.";
      setMsg({ type: "error", text: errMsg });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Persistent Reminder Banner if user closed modal */}
      {!open && !dismissedBanner && (
        <Alert
          severity="warning"
          icon={<ShieldOutlinedIcon />}
          action={
            <Stack direction="row" spacing={1} alignItems="center">
              <Button
                color="inherit"
                size="small"
                variant="outlined"
                onClick={() => setOpen(true)}
                sx={{
                  fontWeight: 800,
                  textTransform: "none",
                  borderRadius: 1.5,
                  fontSize: "0.75rem",
                  borderColor: "warning.main",
                  color: "warning.dark",
                }}
              >
                Change Password Now
              </Button>
              <Button
                color="inherit"
                size="small"
                onClick={() => setDismissedBanner(true)}
                sx={{ fontSize: "0.75rem", textTransform: "none", color: "text.secondary" }}
              >
                Dismiss
              </Button>
            </Stack>
          }
          sx={{
            mb: 2.5,
            borderRadius: 2,
            border: "1px solid",
            borderColor: "warning.light",
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "flex-start", sm: "center" },
            "& .MuiAlert-icon": {
              alignSelf: { xs: "flex-start", sm: "center" },
              mt: { xs: 0.5, sm: 0 },
            },
            "& .MuiAlert-message": {
              width: "100%",
            },
            "& .MuiAlert-action": {
              pt: { xs: 1.5, sm: 0 },
              pl: { xs: 0, sm: 1 },
              alignSelf: { xs: "stretch", sm: "center" },
              width: { xs: "100%", sm: "auto" },
              "& .MuiStack-root": {
                width: { xs: "100%", sm: "auto" },
                justifyContent: { xs: "space-between", sm: "flex-start" },
              },
            },
          }}
        >
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "warning.dark" }}>
              First-Time Security Notice
            </Typography>
            <Typography variant="body2" sx={{ fontSize: "0.85rem", color: "text.secondary" }}>
              You are currently using an auto-generated temporary password from your registration email. For your account security, please create a personalized private password.
            </Typography>
          </Box>
        </Alert>
      )}

      {/* First-Time Password Change Modal */}
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="xs"
        fullWidth
        scroll="paper"
        PaperProps={{
          sx: {
            borderRadius: { xs: 2.5, sm: 3 },
            m: { xs: 1.5, sm: 2 },
            p: 0,
            maxHeight: { xs: "calc(100vh - 24px)", sm: "calc(100vh - 48px)" },
            display: "flex",
            flexDirection: "column",
            boxShadow: "0 24px 48px -12px rgba(11, 59, 96, 0.45)",
            border: "1px solid #E2E8F0",
          },
        }}
      >
        <form onSubmit={handleSubmit}>
          {/* Header */}
          <DialogTitle
            sx={{
              p: { xs: 2, sm: 2.5 },
              pb: 1.5,
              background: "linear-gradient(135deg, #071C2E 0%, #0B3B60 100%)",
              color: "#FFFFFF",
            }}
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  bgcolor: "rgba(255, 255, 255, 0.15)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <LockResetRoundedIcon sx={{ color: "#38BDF8", fontSize: 24 }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="subtitle1" sx={{ fontWeight: 800, color: "#FFFFFF", fontSize: "1.05rem" }}>
                    First-Time Login Setup
                  </Typography>
                  <Chip
                    label="Required"
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: "0.65rem",
                      fontWeight: 800,
                      bgcolor: "rgba(234, 179, 8, 0.25)",
                      color: "#FDE047",
                      border: "1px solid rgba(234, 179, 8, 0.4)",
                    }}
                  />
                </Stack>
                <Typography variant="caption" sx={{ color: "rgba(255, 255, 255, 0.8)", display: "block" }}>
                  Please set a private password to secure your cooperative account
                </Typography>
              </Box>
            </Stack>
          </DialogTitle>

          <DialogContent sx={{ p: { xs: 2, sm: 2.5 }, pt: { xs: 2, sm: 2.5 } }}>
            {msg.text && (
              <Alert severity={msg.type || "info"} sx={{ mb: 2, borderRadius: 2 }}>
                {msg.text}
              </Alert>
            )}

            <Typography variant="body2" sx={{ color: "text.secondary", mb: 2, fontSize: "0.875rem", lineHeight: 1.5 }}>
              Welcome to <strong>Mutual Trust Co-operative</strong>! Because your account was initialized with a temporary password from your registration email, we require you to set a private password before continuing.
            </Typography>

            <Stack spacing={2}>
              {/* Current / Temporary Password */}
              <Box>
                <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary", mb: 0.5, display: "block" }}>
                  Current (Temporary) Password *
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  type={showCurrent ? "text" : "password"}
                  placeholder="Enter the password from your email"
                  value={formData.currentPassword}
                  onChange={(e) => setFormData({ ...formData, currentPassword: e.target.value })}
                  required
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => setShowCurrent((s) => !s)}
                          edge="end"
                          aria-label={showCurrent ? "Hide password" : "Show password"}
                        >
                          {showCurrent ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              {/* Password Generator Helper */}
              <Box
                sx={{
                  p: 1.25,
                  borderRadius: 1.5,
                  bgcolor: alpha(theme.palette.primary.main, 0.04),
                  border: "1px dashed",
                  borderColor: alpha(theme.palette.primary.main, 0.25),
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                }}
              >
                <Box>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: "primary.main", display: "block" }}>
                    Need a strong password?
                  </Typography>
                  <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.7rem" }}>
                    Generate a random 14-character secure password.
                  </Typography>
                </Box>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handleSuggest}
                  startIcon={<AutoAwesomeRoundedIcon fontSize="small" />}
                  sx={{
                    textTransform: "none",
                    fontWeight: 700,
                    fontSize: "0.75rem",
                    borderRadius: 1.5,
                    whiteSpace: "nowrap",
                  }}
                >
                  {copied ? "Copied!" : "Auto-Suggest"}
                </Button>
              </Box>

              {/* New Password */}
              <Box>
                <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary", mb: 0.5, display: "block" }}>
                  New Private Password * (min. 6 characters)
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  type={showNew ? "text" : "password"}
                  placeholder="Create your new strong password"
                  value={formData.newPassword}
                  onChange={(e) => setFormData({ ...formData, newPassword: e.target.value })}
                  required
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => setShowNew((s) => !s)}
                          edge="end"
                          aria-label={showNew ? "Hide password" : "Show password"}
                        >
                          {showNew ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                {formData.newPassword && (
                  <Box sx={{ mt: 1 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                      <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.7rem" }}>
                        Password Strength:
                      </Typography>
                      <Typography variant="caption" sx={{ fontWeight: 800, color: `${strength.color}.main`, fontSize: "0.7rem" }}>
                        {strength.label}
                      </Typography>
                    </Stack>
                    <LinearProgress
                      variant="determinate"
                      value={strength.score}
                      color={strength.color}
                      sx={{ height: 4, borderRadius: 2 }}
                    />
                  </Box>
                )}
              </Box>

              {/* Confirm New Password */}
              <Box>
                <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary", mb: 0.5, display: "block" }}>
                  Confirm New Password *
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  type={showNew ? "text" : "password"}
                  placeholder="Re-type your new password"
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                  required
                  error={Boolean(formData.confirmPassword && formData.newPassword !== formData.confirmPassword)}
                  helperText={
                    formData.confirmPassword && formData.newPassword !== formData.confirmPassword
                      ? "Passwords do not match"
                      : ""
                  }
                />
              </Box>
            </Stack>
          </DialogContent>

          <DialogActions
            sx={{
              p: { xs: 2, sm: 2.5 },
              pt: 1.5,
              borderTop: "1px solid #F1F5F9",
              display: "flex",
              flexDirection: { xs: "column-reverse", sm: "row" },
              justifyContent: { xs: "stretch", sm: "flex-end" },
              gap: 1.25,
              "& button": {
                width: { xs: "100%", sm: "auto" },
              },
            }}
          >
            <Button
              onClick={() => {
                setOpen(false);
                setDismissedBanner(false);
              }}
              color="inherit"
              sx={{ textTransform: "none", color: "text.secondary", fontWeight: 600, py: { xs: 1, sm: 0.75 } }}
            >
              Remind Me Later
            </Button>
            <LoadingButton
              type="submit"
              variant="contained"
              loading={loading}
              startIcon={<CheckCircleRoundedIcon />}
              sx={{
                textTransform: "none",
                fontWeight: 800,
                borderRadius: 2,
                px: 2.5,
                py: { xs: 1.2, sm: 0.75 },
                bgcolor: "#0B3B60",
                "&:hover": { bgcolor: "#071C2E" },
              }}
            >
              Set New Password & Continue
            </LoadingButton>
          </DialogActions>
        </form>
      </Dialog>
    </>
  );
}
